import offlineReleases from "./offlineReleases.json";

export interface ReleaseLink {
  label: string;
  url: string;
}

export interface ReleaseSection {
  title: string;
  items: ReleaseItem[];
  imageGroups?: {
    title?: string;
    images: ReleaseImage[];
  }[];
  fullWidth?: boolean;
}

export interface ReleaseImage {
  alt: string;
  url: string;
}

export interface ReleaseItemDetail {
  heading?: string;
  text?: string;
  image?: ReleaseImage;
  imageSize?: "full" | "small";
}

export type ReleaseItem = string | ReleaseItemDetail;

export interface ReleaseNote {
  version: string;
  label?: string;
  summary: string;
  availability?: string;
  sections: ReleaseSection[];
  links?: ReleaseLink[];
  heroImage?: ReleaseImage;
  platforms?: ("windows" | "macos")[];
  notesPending?: boolean;
  tutorial?: {
    title: string;
    description: string;
    actions: { title: string; location: string; steps: string[]; note?: string }[];
  };
}

export const releaseNotes: ReleaseNote[] = [
  ...offlineReleases,
  {
    version: "2.8.6",
    label: "Latest",
    summary:
      "VR headset view live streaming, Two Zone and Three Zone visual field tests, a richer visual field result sheet with Bebie curve and reliability factor, self-restarting visual field tests, Smooth Pursuit child mode, and the new Oculera Offline Desktop Application.",
    availability: "Spans both website and headset (VR app) updates.",
    heroImage: {
      alt: "Oculera Product Update",
      url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-7-11/header-product-update.png",
    },
    sections: [
      {
        title: "Features",
        fullWidth: true,
        items: [
          {
            heading: "1. Two Zone and Three Zone Visual Field Tests",
            text: "Two new suprathreshold behaviours for faster screening.",
            image: {
              alt: "Two Zone and Three Zone suprathreshold tests",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-6/2-zone-3-zone-suprathreshold.png",
            },
          },
          {
            image: {
              alt: "Two Zone and Three Zone suprathreshold result",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-6/2-zone-3-zone-suprathreshold-2.png",
            },
          },
          {
            heading: "2. Richer Visual Field Result Sheet",
            text: "Bebie curve, mean sensitivity and a reliability factor are now part of the standard visual field report.",
            image: {
              alt: "Visual field result sheet",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-6/visual-field-result-sheet.png",
            },
          },
          {
            heading: "3. Self-Restarting Visual Field Tests",
            text: "When false positives or false negatives run high mid-test, the test restarts on its own instead of producing a result you cannot use.",
            image: {
              alt: "Test restart",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-6/test-restart.png",
            },
          },
          {
            heading: "4. Headset View Live Streaming",
            text: "Watch what the patient sees. The headset view now streams live to the Device page for the whole connection, so you can follow a test from the workstation. Works online and offline. Play and stop on the live view. The streaming widget can be paused rather than running for the whole session.",
            image: {
              alt: "What the patient sees",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-6/what-patient-sees.png",
            },
          },
          {
            heading: "5. Smooth Pursuit Child Mode",
            text: "The target becomes a car driving on a road, with child-friendly assistant videos. The path, the speed and the recorded data are unchanged, so results stay comparable.",
            image: {
              alt: "Smooth Pursuit child mode",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-6/smooth-pursuit-child-mode.png",
            },
          },
          {
            image: {
              alt: "Smooth Pursuit child mode assistant",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-6/smooth-pursuit-child-mode-2.png",
            },
          },
          {
            heading: "6. Spoken Instructions Before Calibration",
            text: "The virtual assistant plays a full instruction video before eye-tracking calibration. You can skip it from the web when the patient already knows the drill.",
            image: {
              alt: "Calibration scene",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-6/calibration-scene.png",
            },
          },
          {
            image: {
              alt: "Calibration instructions",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-6/calibration-scene-2.png",
            },
          },
          {
            heading: "7. Merge Split Sessions",
            text: "If a patient's session was split in two, you can merge it back into a single record straight from the session list.",
            image: {
              alt: "Merge sessions",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-6/merge-sessions.png",
            },
          },
          {
            heading: "8. Headset Menu Status Notifications",
            text: "The web tells you when the headset menu is in the way. A system menu opened on the headset used to look like a frozen test. The Device page now names what is happening.",
            image: {
              alt: "Patient left the test, menu overlay",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-6/patient-left-menu-overlay.png",
            },
          },
          {
            image: {
              alt: "Headset view with controllers",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-6/headset-view-controllers.png",
            },
          },
          {
            heading: "9. Colour Vision in Patient’s Language",
            text: "The colour vision questions are translated and the answer options reworked.",
            image: {
              alt: "Colour vision in the patient’s language",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-6/color-vision-lang.png",
            },
          },
        ],
      },
      {
        title: "Fixes in This Release",
        items: [
          {
            heading: "During a test",
            text: "Improved eye image streaming stability during visual field tests, including settings changes and eye transitions.",
          },
        ],
      },
      {
        title: "Oculera Offline Desktop Application",
        fullWidth: true,
        items: [
          {
            text: "We are excited to introduce the new user interface of the Oculera Offline Desktop Application. It comes with new features and improved accessibility, and is ready to connect with your DICOM worklist, EMR software, and more.",
            image: {
              alt: "DICOM worklist and PACS integration",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-6/dicom-pacs.png",
            },
          },
          "Improved usability and compatibility",
          "Enhanced synchronization with Oculera Cloud",
          "DICOM Worklist integration",
          "PACS integration for seamless connectivity with your clinical systems",
          {
            text: "With these updates, Oculera Desktop provides a more reliable and integrated experience while maintaining the flexibility of running Oculera locally on your computer. We recommend upgrading to Oculera Desktop to take advantage of these new capabilities. If you need assistance with installation, migration, or DICOM/PACS configuration, our team will be happy to support you.",
          },
        ],
      },
    ],
  },
  {
    version: "Offline 2.0.0",
    summary:
      "Oculera Offline Desktop App 2.0.0, released together with 2.8.6: a redesigned offline experience with simpler clinic access, improved system visibility, new maintenance tools, cloud synchronization, DICOM / PACS connectivity and a new offline licence workflow.",
    availability: "Oculera Offline Desktop App for Windows and macOS, released with 2.8.6.",
    heroImage: {
      alt: "Oculera Release Announcement",
      url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-7-11/header-product-update.png",
    },
    sections: [
      {
        title: "Features",
        fullWidth: true,
        items: [
          {
            heading: "1. Background Service, Single Clinic Address & New Status / Diagnostics",
            text: "Oculera Offline Desktop now runs as a background service and starts with the computer. Instead of keeping a separate application window open, operators can access the system from the Windows notification area. Devices on the clinic network can use a single address, http://oculera.local, while the local IP address remains available as a fallback.",
            image: {
              alt: "Background service",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-offline/background-service.png",
            },
          },
          {
            text: "A new Status and Diagnostics panel brings the main system information together in one place, including the clinic address, licence state, network publishing, headset connectivity, services, updates and recent activity.",
            image: {
              alt: "Status panel",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-offline/new-status-panel.png",
            },
          },
          {
            image: {
              alt: "Status panel, services and updates",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-offline/new-status-panel-2.png",
            },
          },
          {
            image: {
              alt: "Status panel, recent activity",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-offline/new-status-panel-3.png",
            },
          },
          {
            heading: "2. New Tray Menu",
            text: "The tray menu is now the main operational entry point for Oculera Offline Desktop. From here, users can open Oculera, copy the clinic address, check licence and publishing status, open diagnostics, manage updates and notifications, allow network access, restart services and open logs.",
            image: {
              alt: "New tray menu",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-offline/new-tray-menu.png",
            },
            imageSize: "small",
          },
          {
            text: "The new Maintenance area also includes database export/import and licence clearing without affecting the patient data stored on the clinic computer.",
            image: {
              alt: "Maintenance screen",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-offline/new-maintenance-screen.png",
            },
          },
          {
            heading: "3. Cloud Synchronization",
            text: "Tests, results and reports continue to work locally without an internet connection. When connectivity is available, Sync database under Device Controls exchanges data between Oculera Offline Desktop and the cloud. Patient and test-result data can move in both directions, while user accounts, permissions and reading layouts are synchronized from the cloud to the clinic.",
            image: {
              alt: "Cloud synchronization",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-offline/cloud-synchronization.png",
            },
          },
          {
            heading: "4. DICOM / PACS Connectivity",
            text: "Oculera Offline Desktop can connect directly to the hospital's imaging systems from the clinic computer. The DICOM / PACS settings support DICOM Send (C-STORE), Modality Worklist (MWL) and Procedure Status (MPPS). Each endpoint can be configured independently and verified with Test connection before going live.",
            image: {
              alt: "DICOM / PACS settings",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-offline/dicom-pacs.png",
            },
          },
          {
            heading: "5. New Offline Licence Workflow",
            text: "Offline licence management now follows a structured request, approval, delivery and activation flow.",
          },
          {
            heading: "Create the request",
            text: "The distributor selects the organization and user, enters the email address(es) that should receive the licence, and chooses the licence expiry period.",
            image: {
              alt: "Create the licence request",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-offline/new-offline-licence-workflow.png",
            },
          },
          {
            heading: "Administrator approval",
            text: "Submitted requests remain pending until they are reviewed. The administrator can accept or reject the request before the licence is issued.",
            image: {
              alt: "Administrator approval",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-offline/administrator-approval.png",
            },
          },
          {
            heading: "Licence email",
            text: "After approval, the licence is sent by email. The message contains the one-time password and the licence file required for activation.",
            image: {
              alt: "Licence email",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-offline/licence-email.png",
            },
          },
          {
            image: {
              alt: "Licence email with the licence file",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-offline/licence-email-2.png",
            },
          },
          {
            heading: "Activate the Oculera system",
            text: "On the activation screen, the clinic uploads the supplied .oclic licence file, enters the licence-file password from the email and selects Activate.",
            image: {
              alt: "Activation screen",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-offline/activation-screen.png",
            },
          },
          {
            heading: "Licence visibility",
            text: "Licence information can also be followed from the Organization Users area, including request history, generated licence date, expiry and last synchronization date.",
            image: {
              alt: "Licence visibility in Organization Users",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-offline/licence-visibility.png",
            },
          },
          {
            heading: "6. Available on Mac",
            text: "Oculera Offline Desktop is also available for Mac environments, extending the same offline workflow beyond Windows.",
            image: {
              alt: "Oculera Offline Desktop on macOS",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-offline/macos-screen.png",
            },
          },
          {
            heading: "7. Platform Features Available Offline",
            text: "Oculera Offline Desktop serves the same Oculera application used in the cloud environment, so recent platform capabilities are also available offline.",
          },
          "Live headset view: operators can follow the eyes and the scene during a test without an internet connection.",
          "Patient guidance: Virtual Assistant videos can be used from the device page and during tests.",
          "Some features require a recent headset software version.",
        ],
      },
    ],
    links: [
      {
        label: "Read the 2.0.0 announcement",
        url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/newsletter/offline_2.0.0.html",
      },
      {
        label: "Windows installation video",
        url: "https://youtu.be/_n-ebfSNrBE",
      },
      {
        label: "macOS installation video",
        url: "https://youtu.be/r5JG_AR4FQs",
      },
    ],
  },
  {
    version: "2.8.5",
    summary:
      "A major product update introducing the Test Suite workflow, the new Device Page, the Esterman-3 140° visual field test, in-test pause and resume, and broad language, streaming, and reliability improvements.",
    availability: "Spans both website and headset (VR app) updates.",
    heroImage: {
      alt: "Oculera Product Update",
      url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-7-11/header-product-update.png",
    },
    sections: [
      {
        title: "Features",
        fullWidth: true,
        items: [
          {
            heading: "1. Esterman-3 140° Visual Field Test",
            text: "A new Esterman-3 140° Visual Field Test is added to the Visual Field test category. This test option lets users perform a wider visual field assessment using the Esterman-3 test pattern with a 140° field range. It is available under the Create New Test screen by selecting Test Type.",
            image: {
              alt: "Esterman-3 140° Visual Field Test",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-5/image_esterman.png",
            },
          },
          {
            heading: "2. Pause Test",
            text: "A new Pause / Unpause feature is added to the Visual Field Analyzer test. During an active Visual Field Analyzer test, users can pause and resume the test directly from the web panel. When the test is paused, the device also reflects the paused state, and the test continues only after the user selects Unpause.",
            image: {
              alt: "Pause test from the web panel",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-5/pause_test_web.png",
            },
          },
          {
            image: {
              alt: "Pause test in the editor",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-5/pause_test_editor.png",
            },
          },
          {
            heading: "3. Test Suite Workflow",
            text: "A new Test Suite Workflow improves multi-test session management. Users can choose between Single Test, which runs one selected test only, and Session / Test Suite, which runs a predefined set of multiple tests in sequence. The test mode selection is available directly in the Create New Test flow. In the Test Suite screen, users can select a predefined suite, review the test queue, update test configurations, and start the full sequence. The suite workflow also supports Auto Play, allowing tests to continue through the queue more smoothly. Session creation is deferred until the first result is received.",
            image: {
              alt: "Start a new session",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-5/start_a_new_session.png",
            },
          },
          {
            image: {
              alt: "Test suite configuration page",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-5/suite_config_page.png",
            },
          },
          {
            heading: "4. Device Management",
            text: "The old Dashboard is replaced with the new Device Page. Users can manage device-related actions from a single page, including checking device status, starting calibration, ending an active test, installing Virtual Assistant videos, and accessing version control information.",
            image: {
              alt: "Device page",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-5/usertest_device_page.png",
            },
          },
          {
            text: "4 point eye tracking calibration.",
            image: {
              alt: "4 point eye tracking calibration",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-5/usertest_calibrate_ss.png",
            },
          },
          {
            heading: "5. Bulk Patient Import",
            text: "Import patients directly from Excel spreadsheets.",
            image: {
              alt: "Bulk patient import",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-5/bulk_patient_import.png",
            },
          },
          {
            heading: "6. Landing Page",
            text: "The web application landing page is updated.",
            image: {
              alt: "Updated landing page",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-5/landing_page.png",
            },
          },
          {
            heading: "7. Eye Images During FDT Tests",
            text: "Eye camera images are visible during Frequency Doubling Technology (FDT) test progress.",
          },
          {
            heading: "8. Left / Right Eye Testing Improvements",
            text: "Additional eye-specific testing workflow improvements. You can now test both eyes sequentially. You do not need to select one eye and then the other to start the test.",
            image: {
              alt: "Right then left eye testing",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-5/right_then_left.png",
            },
          },
          {
            heading: "9. Fixation Point Movement Visualization",
            text: "A new fixation target movement animation is added. When the fixation target position changes during a test, users can see the target moving from its previous position to the new position with an animation. This makes the target adjustment easier to follow during the test.",
            image: {
              alt: "Fixation point movement",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-5/fixation_point_movement.png",
            },
          },
          {
            heading: "10. Swedish Translation",
            text: "Added Swedish language support and localization updates.",
            image: {
              alt: "Swedish translation",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-5/swedish.png",
            },
          },
          {
            heading: "11. New VA languages",
            text: "The Virtual Assistant supports the following additional languages: Portuguese, Russian, Polish, Dutch, Bulgarian, Hungarian, Danish, Norwegian, and Arabic.",
            image: {
              alt: "New Virtual Assistant languages",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-5/va_lang.png",
            },
          },
          {
            heading: "12. DICOM Pipeline",
            text: "DICOM export is improved for more compatibility.",
            image: {
              alt: "DICOM export",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-5/export_dicom.png",
            },
          },
          {
            heading: "13. Controller Battery Information",
            text: "Controller battery information is displayed correctly in the headset. Users can see the VR controller battery level more reliably during device usage and testing.",
          },
          {
            heading: "14. VR Status Icons",
            text: "New status indicators are added inside the headset. The headset shows status icons for internet connection, server connection, controller connection, and a warning state when no controller is connected. These indicators help users and operators quickly understand the current device and connection status during testing.",
            image: {
              alt: "VR status icons",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-5/vr_icons.png",
            },
          },
          {
            heading: "15. Fixed a Stereoacuity test alignment bug.",
          },
          {
            heading: "16. Device Selection",
            text: "Device Selection is now located in the top menu, so you can always see which device is currently selected. The separate device selection page has been removed.",
          },
          {
            heading: "17. Security Improvements",
            text: "Multiple security and platform-hardening improvements were implemented, along with authentication and application reliability enhancements.",
          },
        ],
      },
      {
        title: "Important Bug Fixes",
        fullWidth: true,
        items: [
          {
            heading: "1. Eye Tracking Sometimes Stops Working",
            text: "We identified an issue where the eye-tracking sensors could stop working after the headset entered sleep mode and then woke up again. When this happened, some internal configurations related to eye tracking were not restored correctly, causing unreliable behavior. To resolve this issue, we disabled both headset sleep mode and automatic screen shutdown. Instead of turning the display off, the headset now switches to an idle screen that shows only the Oculera logo. This reduces power consumption compared to keeping the full application active, while ensuring that the eye-tracking system remains functional. This solution does increase battery usage compared to the previous setup, but it is currently the most reliable way to prevent eye-tracking failures caused by sleep and wake cycles.",
          },
          {
            heading: "2. Eye Video Stream Failures",
            text: "The headset continuously streams both eye video data and eye movement data in real time. We identified reliability issues in the backend infrastructure responsible for receiving and processing these streams. While the streaming functionality itself was working, the backend services handling the real-time data were not always reliable across different network environments. In some cases, network restrictions or configuration differences could interrupt the connection, causing eye video streams and eye movement data to fail or become unstable. To resolve this, we developed and deployed a completely new server infrastructure specifically designed for real-time eye data streaming. The new backend architecture provides more robust connection handling, improved reliability, and better compatibility across different network environments. As a result, both the eye video stream and real-time eye movement data transmission are now significantly more stable and dependable.",
          },
        ],
      },
      {
        title: "New Video Tutorial: Test Suite",
        items: [
          {
            text: "Want to learn more about managing sessions? We've put together a comprehensive video tutorial walking you through the new Test Suite workflow. Discover how to easily set up predefined tests, update configurations, and use the Auto Play feature to streamline your sessions.",
            image: {
              alt: "Test Suite video tutorial",
              url: "https://img.youtube.com/vi/7L79sSRFiIs/hqdefault.jpg",
            },
          },
        ],
      },
    ],
    links: [
      {
        label: "Watch the Test Suite Tutorial on YouTube",
        url: "https://youtu.be/7L79sSRFiIs",
      },
    ],
  },
  {
    version: "2.8.4",
    summary: "Oculera Product Update.",
    heroImage: {
      alt: "Oculera Product Update",
      url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-7-11/header-product-update.png",
    },
    sections: [
      {
        title: "Features",
        fullWidth: true,
        items: [
          {
            heading: "1. Objective Phoria testing",
            text: "Objective Phoria can now be selected under Cover Tests. This is an additional cover test type that provides a more detailed assessment and takes slightly longer than the standard Cover-Uncover test.",
            image: {
              alt: "Objective Phoria testing",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release+2.8.4/1.png",
            },
          },
          {
            heading: "2. Test both eyes back to back",
            text: "For Visual Field testing, operators can now select both the right and left eye before the test begins. Once the right-eye test is completed, the left-eye test starts automatically without interruption or additional operator input, making the workflow faster and more efficient.",
            image: {
              alt: "Test both eyes back to back",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release+2.8.4/2.png",
            },
          },
          {
            heading: "3. Tumbling E threshold testing with corrected sizing",
            text: "Threshold testing is now available for the Tumbling E test. Instead of always starting from the beginning of the chart, the test starts from a mid-level size. If the patient cannot identify the optotype, the size increases; if they can, it decreases. This makes the test faster and more efficient.",
          },
          {
            heading: "4. VA skip video logic with confirmation modal",
            text: "The Visual Assistant video can now be skipped after the test has started, with a confirmation modal shown before skipping.",
            image: {
              alt: "VA skip video logic with confirmation modal",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-1/B1.png",
            },
          },
          {
            heading: "5. Eye-tracking calibration integrated into the app",
            text: "Eye-tracking calibration can now be performed without leaving the application. In the menu scene, calibration can be triggered by pressing B five times on the controller. It can also be launched from the Dashboard. This is especially useful when there is a high fixation loss rate during testing.",
            image: {
              alt: "Eye-tracking calibration integrated into the app",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release+2.8.4/5.png",
            },
            imageSize: "small",
          },
          {
            heading: "6. Color Vision (Ishihara) test controlled by controller",
            text: "A new Color Vision (Ishihara) test has been added that does not require operator assistance. The patient can provide responses directly using the controller thumbstick.",
            image: {
              alt: "Color Vision (Ishihara) test controlled by controller",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-1/A-4.png",
            },
          },
          {
            heading: "7. Improved Color Vision results with deficiency types",
            text: "Color Vision test results now include deficiency type classifications, such as red-green deficiency.",
          },
          {
            heading: "8. Contrast Sensitivity Test improvements (FN/FP logic, delays)",
            text: "False negative and false positive calculations have been added to the Contrast Sensitivity threshold test, along with timing and delay improvements.",
          },
          {
            heading: "9. Trigger button animation before test start",
            text: "A tutorial-style animation showing the trigger button is now displayed before the test begins to guide the patient.",
            image: {
              alt: "Trigger button animation before test start",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-1/B5.png",
            },
            imageSize: "small",
          },
          {
            heading: "10. Test finish animation added",
            text: "A completion animation is now shown in the app after a test is finished.",
          },
          {
            heading: "11. User settings page",
            text: "A new user settings page has been added. From Settings > User, users can change their password, enable two-factor authentication, view their registered email address, and update their email address. The registered email address is also used for two-factor authentication when enabled.",
          },
          {
            heading: "12. Assign test to another patient",
            text: "Completed tests can now be reassigned to another patient.",
            image: {
              alt: "Assign test to another patient",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release+2.8.4/12.png",
            },
          },
          {
            heading: "13. Ocular Motility and Pupillometry results explanation card",
            text: "OM and PL results now include explanatory result cards.",
          },
          {
            heading: "14. Load ongoing test progress after refresh",
            text: "If the browser tab is accidentally closed during a Visual Field test, the operator can reopen the website, it automatically reconnect to the device, and continue viewing the ongoing test progress without losing it.",
          },
          {
            heading: "15. Improved test saving logic",
            text: "Test saving has been significantly improved. If there is a temporary connection issue, whether due to internet connectivity or communication between the headset and the server, completed tests are stored locally first. Once the connection is restored, the data is saved to the database. This helps prevent test data loss.",
          },
          {
            heading: "16. VA videos pre-downloaded during installation",
            text: "In the offline version, Virtual Assistant videos can now be installed in advance during setup while internet access is available. Once downloaded to the VR headset, they can be used offline.",
          },
          {
            heading: "17. Device removal request for distributors",
            text: "Distributors can now remove devices from an account through the distributor panel.",
          },
          {
            heading: "18. Offline license information display for organizations",
            text: "Offline license details are now visible in both the Distributor panel and Organization pages.",
          },
          {
            heading: "19. Fovea threshold visibility improvement",
            text: "The fovea threshold is now displayed once it has been calculated.",
            image: {
              alt: "Fovea threshold visibility improvement",
              url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release+2.8.4/19_cropped.png",
            },
          },
          {
            heading: "20. Eye video stream on pupillometry",
          },
        ],
      },
      {
        title: "Fixes",
        fullWidth: true,
        items: [
          "21. Monocular test covered-eye black screen fix: For Visual Acuity, Color Vision, and Pelli Robson tests, the covered eye is now obscured with a black screen instead of a white screen.",
          "22. Stereoacuity controller bug fix (2.7.13): A controller-related issue affecting the Stereoacuity test in version 2.7.13 has been fixed.",
          "23. Offline sync fix: An issue affecting offline database synchronization between the cloud and offline systems in previous versions has been resolved.",
        ],
      },
    ],
  },
  {
    version: "2.8.1",
    summary:
      "A mixed website and VR application update focused on multilingual testing support, reporting, PDF quality, and workflow polish.",
    availability:
      "Some items are available immediately on the website; headset-specific items require an app update.",
    heroImage: {
      alt: "Oculera product update header",
      url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-7-11/header-product-update.png",
    },
    sections: [
      {
        title: "Available Without App Update",
        items: [
          "Multi-language virtual assistant support for VFA across Arabic, German, English, Spanish, Persian, French, Italian, Japanese, Turkish, Urdu, Simplified Chinese, Czech, Romanian, Korean, Vietnamese, and Chinese Cantonese/Mandarin.",
          "A new Visual Assistant report button for reporting translation, pronunciation, or video issues and suggesting edits.",
          "Patient ID validation now prevents saving patients without an ID, resolving empty-ID edit issues.",
          "Color Vision results now include a clearer summary.",
          "Visual field PDF exports are smaller.",
          "Saved comments now appear in results and in exported PDFs.",
          "Timezone-related date shifts have been corrected.",
        ],
        imageGroups: [
          {
            title: "Multi-language VA support",
            images: [
              {
                alt: "Multilingual virtual assistant examples",
                url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-1/A1-1.png",
              },
              {
                alt: "Virtual assistant language support example",
                url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-1/A1-2.png",
              },
              {
                alt: "Virtual assistant language support example",
                url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-1/A1-3.png",
              },
            ],
          },
          {
            title: "Website-side improvements",
            images: [
              {
                alt: "Visual assistant reporting UI",
                url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-1/A2.png",
              },
              {
                alt: "Improved color vision result summary",
                url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-1/A-4.png",
              },
            ],
          },
        ],
      },
      {
        title: "Requires App Update",
        items: [
          "Visual Assistant videos can be skipped after test start, with a confirmation modal.",
          "Eye-tracking calibration is now integrated into the app and can also be triggered from the dashboard.",
          "The Ishihara Color Vision test can now be completed directly with the controller.",
          "Contrast Sensitivity testing now includes false-negative and false-positive calculations.",
          "A trigger-button tutorial animation appears before the test starts.",
          "A finish animation now appears after test completion.",
          "Covered-eye monocular tests now use a black screen instead of a white screen for Visual Acuity, Color Vision, and Pelli Robson.",
        ],
        imageGroups: [
          {
            title: "App update visuals",
            images: [
              {
                alt: "Integrated eye-tracking calibration view",
                url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-1/B1.png",
              },
              {
                alt: "Controller-driven color vision test",
                url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-1/B2.png",
              },
              {
                alt: "Monocular black screen improvement",
                url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-8-1/B5.png",
              },
            ],
          },
        ],
      },
    ],
  },
  {
    version: "2.7.13",
    summary:
      "A clinically significant release that expands perimetry coverage and adds operator-facing controls for fixation monitoring, response quality, and headset audio.",
    availability:
      "Introduces new tests, richer progress information, and previews the upcoming Test Suite workflow.",
    heroImage: {
      alt: "Oculera 2.7.13 update header",
      url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-7-11/header-product-update.png",
    },
    sections: [
      {
        title: "New Tests",
        items: [
          "Blue on Yellow Perimetry (SWAP) for early visual field defect detection, with blue stimuli on a yellow adaptation field and support for 24-2 and 30-2 patterns.",
          "Automated Kinetic Perimetry with configurable threshold levels, patterns, stimulus sizes, speeds, and meridians for peripheral field mapping.",
        ],
        imageGroups: [
          {
            images: [
              {
                alt: "SWAP result and configuration",
                url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-7-12/swap.png",
              },
              {
                alt: "Kinetic perimetry result",
                url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-7-12/kinetic-png.png",
              },
            ],
          },
        ],
      },
      {
        title: "New Features",
        items: [
          "Live patient eye video stream during visual field testing, alongside gaze graph visibility, fixation-loss warnings, and optional auto-pause.",
          "Tumbling E was updated to a precise 5x5 optotype grid for better visual acuity accuracy.",
          "False Positive and False Negative rates are now displayed on the test progress page when invalid responses occur.",
          "Headset volume can be adjusted from the website before and during testing.",
        ],
        imageGroups: [
          {
            images: [
              {
                alt: "Eye video stream feature",
                url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-7-12/eye-video-stream.png",
              },
              {
                alt: "False positive and false negative tracking",
                url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-7-12/false-positive.png",
              },
              {
                alt: "Headset volume configuration screen",
                url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-7-12/volume-png.png",
              },
            ],
          },
        ],
      },
      {
        title: "Platform Scope",
        items: [
          "Expanded visual field library with threshold, suprathreshold, Esterman binocular, and ptosis protocols.",
          "Automated kinetic perimetry patterns FF-60-12, FF-50-12, and FF-30-8.",
          "FDT, SWAP, Visual Acuity, Color Vision, and Amsler Grid remain part of the active test library.",
          "Results continue to include MD, PSD, GHT, VFI, GPA, DICOM/PDF outputs, HFA import, and cloud storage.",
        ],
      },
      {
        title: "Coming Soon",
        items: [
          "A Test Suite system for running predefined or custom multi-test sequences automatically.",
          "Suite-driven patient guidance inside the headset to reduce operator intervention across queued tests.",
        ],
        imageGroups: [
          {
            images: [
              {
                alt: "Test suite interface preview",
                url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-7-12/test-suitepng.png",
              },
            ],
          },
        ],
      },
    ],
  },
  {
    version: "2.7.12",
    summary:
      "A release centered on new clinical capabilities, especially SWAP, automated kinetic perimetry, and eye video streaming for operators.",
    availability:
      "Framed as a clinical advancement update with expanded specifications and streamlined workflow support.",
    heroImage: {
      alt: "Oculera 2.7.12 update header",
      url: "https://stratus.campaign-image.com/images/1769371792562_1_zc_v1_1598913000000158036.png",
    },
    sections: [
      {
        title: "Feature Deep Dive",
        items: [
          "Blue on Yellow Perimetry (SWAP) uses blue stimuli on a bright yellow background to detect early visual field defects through short-wavelength pathway testing.",
          "Eye Video Stream enables real-time eye-position monitoring with gaze graph visibility, fixation-loss warnings, and auto-pause support.",
        ],
        imageGroups: [
          {
            images: [
              {
                alt: "SWAP visual field image",
                url: "https://stratus.campaign-image.com/images/1769371794498_2_zc_v1_1598913000000158036.png",
              },
              {
                alt: "Eye video stream image",
                url: "https://stratus.campaign-image.com/images/1769371795444_3_zc_v1_1598913000000158036.png",
              },
            ],
          },
        ],
      },
      {
        title: "Clinical Coverage",
        items: [
          "Visual Field threshold tests: 24-2, 30-2, and 10-2.",
          "Strategies include Oculera Interactive, Fast Tracking, Supra-Threshold, and Full-Threshold.",
          "Suprathreshold protocols include C40, C76, and Full-Field 120, alongside Esterman binocular and ptosis tests.",
          "Kinetic Perimetry adds FF-60-12, FF-50-12, and FF-30-8.",
          "Visual Acuity, Color Vision, Amsler Grid, fixation control, GPA metrics, DICOM/PDF export, HFA import, and cloud storage remain available.",
        ],
      },
    ],
  },
  {
    version: "2.7.11",
    summary:
      "A broad workflow update that introduced the virtual assistant, Tumbling E, refreshed patient/test UI, and distributor-side web installation.",
    availability:
      "Includes both user-facing functionality and operational guidance for installation, offline packages, and package eligibility.",
    heroImage: {
      alt: "Oculera 2.7.11 update header",
      url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-7-11/header-product-update.png",
    },
    sections: [
      {
        title: "Major Additions",
        items: [
          "Sia, the Oculera virtual assistant, now guides patients at test start in both the VR app and website flow.",
          "Tumbling E visual acuity testing was added for controller-driven testing without operator interaction.",
          "The test list and patient page received a UI refresh and search bug fixes.",
          "Distributors can install, update, and uninstall the Oculera APK directly from the web installer.",
        ],
        imageGroups: [
          {
            title: "Virtual assistant and testing",
            images: [
              {
                alt: "Virtual assistant Sia",
                url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-7-11/virtual-assistant-webp.webp",
              },
              {
                alt: "Test options screen",
                url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-7-11/test-options-png.png",
              },
            ],
          },
          {
            title: "UI refresh",
            images: [
              {
                alt: "New test list UI",
                url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-7-11/test-list-png.png",
              },
              {
                alt: "Patient page UI",
                url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-7-11/patient-page-png.png",
              },
            ],
          },
        ],
      },
      {
        title: "Operational Notes",
        items: [
          "The web installer flow covers device connection, serial number copying, version download, installation, and config setup from `oculera.health/installer`.",
          "Real-time eye tracking graphs for VFA and FDT remain available from the earlier 2.6.7 release.",
          "The Czech and Romanian language issue present in 2.6.7 was fixed in this update.",
          "Logged-in users are prompted about available device updates from the New Test page, and offline packages also need updating.",
        ],
        imageGroups: [
          {
            images: [
              {
                alt: "How to update visual guide",
                url: "https://oculera-marketing.s3.eu-central-1.amazonaws.com/release-2-7-11/how-to-update-png.png",
              },
            ],
          },
        ],
      },
      {
        title: "Commercial Notes",
        items: [
          "Headset app updates do not automatically enable every test package for every user.",
          "Existing clients upgrading to the Oculera Full package must pay the upgrade fee.",
          "Basic package access for earlier purchases depends on maintenance-fee status and the January 1, 2025 cutoff noted in the release email.",
        ],
      },
    ],
    links: [
      {
        label: "Web Installer Tutorial",
        url: "https://www.youtube.com/watch?v=DHzFoWngCgU",
      },
      {
        label: "Offline Package Download",
        url: "https://drive.google.com/drive/folders/1coFydC2l5A0nDbZzNOflk-Vt6aEfQSnt?usp=sharing",
      },
    ],
  },
];
